import React, { useMemo, useState } from 'react';
import PhotoCollecte from '../PhotoCollecte';
import { Chargement } from '../charte/Elements';
import { CHARTE, COULEURS } from '../../theme/charte';
import type { ChampMission } from '../../donnees/missions';
import {
  Collecte, nomPhoto, normaliserLibelle, photosDeLaCollecte, reponsesParLibelle, texteReponse,
} from '../../donnees/collectes';
import { joursDepuis, jourCourt } from '../../donnees/situation';

/* Onglet Résultats de la fiche, d'après la maquette MissionResultats :
   filtre par état en segments, recherche, liste à colonnes (photo, lieu,
   réponses, fraîcheur, état), pages de 25 lieux. Les colonnes viennent des
   informations relevées de la mission ; les réponses oui / non sont
   regroupées en pastilles dans une seule colonne. */

const PAR_PAGE = 25;

/* Teintes de texte de la maquette pour les états (lisibles sur la surface). */
const VERT_TEXTE = '#356A0F';
const ORANGE_TEXTE = '#A23A0D';

const ETATS: Record<string, { libelle: string; point: string; texte: string }> = {
  valide: { libelle: 'Validé', point: COULEURS.succes, texte: VERT_TEXTE },
  en_attente: { libelle: 'En vérification', point: CHARTE.ocre, texte: CHARTE.ocreTexte },
  rejete: { libelle: 'Rejeté', point: COULEURS.erreur, texte: ORANGE_TEXTE },
  expire: { libelle: 'Expiré', point: COULEURS.neutre, texte: COULEURS.neutre },
};
const ETAT_INCONNU = { libelle: 'État inconnu', point: COULEURS.neutre, texte: COULEURS.neutre };

const FILTRES: { cle: string; libelle: string }[] = [
  { cle: 'tous', libelle: 'Tous' },
  { cle: 'valide', libelle: 'Validés' },
  { cle: 'en_attente', libelle: 'En vérification' },
  { cle: 'rejete', libelle: 'Rejetés' },
];

const libelleChamp = (c: ChampMission) => c.label || c.nom || '';
const estOuiNon = (c: ChampMission) => c.type === 'booleen';
const sansAccents = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const Pastille: React.FC<{ libelle: string; valeur: string }> = ({ libelle, valeur }) => {
  const oui = valeur === 'Oui', non = valeur === 'Non';
  return (
    <span className="pastille-reponse" style={{
      background: oui ? 'rgba(95,140,32,0.14)' : non ? 'rgba(212,74,15,0.12)' : 'rgba(90,82,69,0.10)',
      color: oui ? VERT_TEXTE : non ? ORANGE_TEXTE : COULEURS.neutre,
    }}>
      {libelle} {oui ? '✓' : non ? '✗' : '—'}
    </span>
  );
};

const ResultatsMission: React.FC<{
  collectes: Collecte[];
  champs: ChampMission[];
  chargees: boolean;
}> = ({ collectes, champs, chargees }) => {
  const [filtre, setFiltre] = useState('tous');
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(0);

  const champsPhoto = champs.filter(c => c.type === 'photo' && libelleChamp(c));
  const champsOuiNon = champs.filter(c => estOuiNon(c) && libelleChamp(c));
  const champsTexte = champs.filter(c => c.type !== 'photo' && !estOuiNon(c) && libelleChamp(c));
  const [champLieu, ...autresChamps] = champsTexte;

  const lignes = useMemo(() => [...collectes]
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
    .map(c => ({ collecte: c, reponses: reponsesParLibelle(c.reponsesJson) })), [collectes]);

  const nb = (cle: string) => cle === 'tous' ? lignes.length : lignes.filter(l => l.collecte.statut === cle).length;
  const terme = sansAccents(recherche.trim());
  const visibles = lignes
    .filter(l => filtre === 'tous' || l.collecte.statut === filtre)
    .filter(l => !terme || sansAccents(Array.from(l.reponses.values()).map(texteReponse).join(' ')).includes(terme));
  const pages = Math.max(1, Math.ceil(visibles.length / PAR_PAGE));
  const pageCourante = Math.min(page, pages - 1);
  const affichees = visibles.slice(pageCourante * PAR_PAGE, (pageCourante + 1) * PAR_PAGE);

  const reponse = (r: Map<string, unknown>, c: ChampMission) => r.get(normaliserLibelle(libelleChamp(c)));

  const colonnes = [
    '64px',
    'minmax(0, 2fr)',
    ...autresChamps.map(() => 'minmax(0, 1.2fr)'),
    ...(champsOuiNon.length > 0 ? ['minmax(0, 1.4fr)'] : []),
    'minmax(0, 0.9fr)',
    'minmax(0, 0.8fr)',
  ];
  const largeurMin = Math.max(760, colonnes.length * 150);

  const choisir = (cle: string) => { setFiltre(cle); setPage(0); };

  return (
    <div className="pile" style={{ gap: '24px' }}>
      <div className="ligne ligne--entre" style={{ gap: '12px' }}>
        <div className="segments" role="group" aria-label="Filtrer par état">
          {FILTRES.map(f => (
            <button key={f.cle} type="button" aria-pressed={filtre === f.cle} onClick={() => choisir(f.cle)}>
              {f.libelle} · {chargees ? nb(f.cle).toLocaleString('fr-FR') : '…'}
            </button>
          ))}
        </div>
        <label className="recherche">
          <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COULEURS.texteDiscret} strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
          <span className="visuellement-cache">Rechercher un lieu</span>
          <input type="search" placeholder="Nom, quartier, responsable…" value={recherche}
            onChange={e => { setRecherche(e.target.value); setPage(0); }} />
        </label>
      </div>

      {!chargees ? <Chargement /> : (
        <div className="liste">
          <div className="liste-defilante">
            <div style={{ '--colonnes': colonnes.join(' '), '--largeur-min': `${largeurMin}px` } as React.CSSProperties} className="liste-defilante__contenu">
              <div className="liste__entete liste__entete--serree" aria-hidden>
                <div>Photo</div>
                <div>{champLieu ? libelleChamp(champLieu) : 'Lieu'}</div>
                {autresChamps.map(c => <div key={libelleChamp(c)}>{libelleChamp(c)}</div>)}
                {champsOuiNon.length > 0 && <div>{champsOuiNon.map(libelleChamp).join(' · ')}</div>}
                <div>Fraîcheur</div>
                <div>État</div>
              </div>
              {affichees.length === 0 ? (
                <div className="liste__vide">{collectes.length === 0 ? 'Aucune collecte pour le moment.' : 'Aucun lieu ne correspond à ce filtre.'}</div>
              ) : affichees.map(({ collecte: c, reponses: r }) => {
                const etat = ETATS[c.statut ?? ''] ?? ETAT_INCONNU;
                const photos = champsPhoto.length > 0
                  ? champsPhoto.flatMap(ch => {
                      const v = reponse(r, ch);
                      return (Array.isArray(v) ? v : [v]).map(nomPhoto).filter((n): n is string => !!n);
                    })
                  : photosDeLaCollecte(c);
                const age = joursDepuis(c.createdAt);
                return (
                  <div key={c.id} data-testid={`collecte-${c.id}`} className="liste__ligne liste__ligne--serree"
                    style={{ opacity: c.statut === 'en_attente' ? 0.75 : undefined }}>
                    <div data-label="Photo" className="ligne" style={{ gap: '4px', flexWrap: 'nowrap' }}>
                      {photos.length === 0
                        ? <span className="vignette-vide" aria-hidden />
                        : <PhotoCollecte nom={photos[0]} taille={56} hauteur={44}
                            legende={`Photo 1 de la collecte du ${jourCourt(c.createdAt)}`} />}
                      {photos.length > 1 && <span className="petit texte-secondaire">+{photos.length - 1}</span>}
                    </div>
                    <div className="liste__principal" style={{ gap: '2px' }}>
                      <span style={{ fontWeight: 600, fontSize: '14px' }}>{champLieu ? texteReponse(reponse(r, champLieu)) : `Lieu relevé le ${jourCourt(c.createdAt)}`}</span>
                      <span style={{ fontSize: '12px', color: COULEURS.texteDiscret }}>Relevé le {jourCourt(c.createdAt)}</span>
                    </div>
                    {autresChamps.map(ch => (
                      <div key={libelleChamp(ch)} data-label={libelleChamp(ch)} style={{ fontSize: '14px', overflowWrap: 'anywhere' }}>
                        {texteReponse(reponse(r, ch))}
                      </div>
                    ))}
                    {champsOuiNon.length > 0 && (
                      <div data-label="Réponses oui / non" className="ligne" style={{ gap: '6px' }}>
                        {champsOuiNon.map(ch => <Pastille key={libelleChamp(ch)} libelle={libelleChamp(ch)} valeur={texteReponse(reponse(r, ch))} />)}
                      </div>
                    )}
                    <div data-label="Fraîcheur" style={{ fontSize: '14px' }}>{age != null ? `${age} j` : '—'}</div>
                    <div data-label="État">
                      <span className="etat-point" style={{ color: etat.texte }}>
                        <span aria-hidden style={{ background: etat.point }} />{etat.libelle}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {visibles.length > 0 && (
            <div className="liste__pied">
              <div>
                {pageCourante * PAR_PAGE + 1} à {Math.min(visibles.length, (pageCourante + 1) * PAR_PAGE)} sur {visibles.length.toLocaleString('fr-FR')} lieu{visibles.length > 1 ? 'x' : ''}
                {filtre === 'tous' && nb('en_attente') > 0 ? ` · ${nb('en_attente').toLocaleString('fr-FR')} en vérification` : ''}
              </div>
              <div className="ligne" style={{ gap: '6px' }}>
                <button type="button" className="bouton-page" onClick={() => setPage(pageCourante - 1)} disabled={pageCourante === 0}>Précédent</button>
                <button type="button" className="bouton-page bouton-page--fort" onClick={() => setPage(pageCourante + 1)} disabled={pageCourante >= pages - 1}>Suivant</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ResultatsMission;
